import React, { createContext, useContext } from 'react';
import { isObject } from 'lodash-es';

// Types pour les événements utilisateur
export type ActionEventUser = {
    user_id: string;
    user_type: 'affaires' | 'résidentiel' | 'VGE' | null;
    user_account_count: number | null;
    user_active_account_count: number | null;
};

type ActionEventInteraction = {
    // null = clic sans URL cible (onglet, changement de langue), valeur attendue par GTM
    link_url?: string | null;
    link_text?: string;
    file_name?: string;
    file_extension?: string;
    file_type?: string;
    form_name?: string;
    validation_status?: 'admissible';
    click_type?:
    | 'menu principal'
    | 'menu secondaire'
    | 'bouton'
    | 'onglet'
    | 'adresse courriel'
    | 'lien interne'
    | 'lien sortant'
    | 'numéro de téléphone';
    search_term?: string;
    search_results_count?: number;
};

export type SelectionContratEvent = {
    contract_type: string;
    click_text: string;
    click_url: string;
};

export type ContractEventName =
    | 'selection_contrat'
    | 'contract_new'
    | 'prev_update'
    | 'nomin_update'
    | 'contract_renewal';

export type ContractEventPayload = {
    contract_type: string;
    contract_action?: 'future_new' | 'future_update' | 'renewal_new' | 'renewal_update' | 'current';
    click_text?: string;
    click_url?: string;
};

// Types pour les actions de contrat (copié de nec-front contractAction.ts)
type LocationLike = {
    pathname?: string;
    state?: unknown;
};

type ContractAction = 'future_new' | 'future_update' | 'renewal_new' | 'renewal_update' | 'current';

// Vérifie si on est dans le flow de finalisation PF (copié de nec-front contractAction.ts)
export function isFromFutureNewFlow(location: LocationLike): boolean {
    const pathname = location?.pathname ?? '';
    const state = (location?.state as { fromFinaliserPF?: boolean } | null) ?? null;

    return (
        pathname.includes('/finaliser-pf/') ||
        pathname.includes('/contrats/nouveau/') ||
        Boolean(state?.fromFinaliserPF)
    );
}

// Détermine l'action du contrat selon le statut (copié de nec-front contractAction.ts)
export function getContractActionFromStatutContrat(
    statutContrat: number | null | undefined,
    fromFutureNewFlow: boolean,
): ContractAction {
    if (fromFutureNewFlow) return 'future_new';
    if (statutContrat === 1) return 'current';
    if (statutContrat === 4) return 'future_update';
    if (statutContrat === 3) return 'renewal_update';
    if (statutContrat === 2) return 'renewal_new';
    return 'renewal_new';
}

type ActionEvent =
    | {
        event: 'user_interaction';
        action: {
            action: string;
            label: string;
        };
    }
    | {
        event: 'user_interaction';
        event_data: {
            event_name: string;
            user?: ActionEventUser;
        };
    }
    | {
        event: 'user_interaction';
        event_data: {
            event_name: string;
            interaction?: ActionEventInteraction;
        };
    };

// Contexte TagManager
interface TagManagerContextValue {
    actionEvent: (name: string | { action: string; label: string }, event?: ActionEventUser) => void;
    interactionEvent: (name: string, event: ActionEventInteraction) => void;
    contractEvent: (eventName: ContractEventName, payload: ContractEventPayload) => void;
    selectionContratEvent: (event: SelectionContratEvent) => void;
    getCategory: () => string;
}

const TagManagerContext = createContext<TagManagerContextValue | null>(null);

export function useTagManager() {
    const context = useContext(TagManagerContext);

    if (!context) {
        throw new Error("Le hook useTagManager doit être utilisé à l'intérieur de TagManagerProvider.");
    }

    return context;
}

// Fonctions de tracking exportées
export function actionEvent(name: string | { action: string; label: string }, event?: ActionEventUser): void {
    if (typeof window === 'undefined' || !window.dataLayer) return;

    if (isObject(name) && 'action' in name) {
        const enhancedEvent: ActionEvent = {
            event: 'user_interaction',
            action: name,
        };

        window.dataLayer.push(enhancedEvent);
        return;
    }

    const enhancedEventWithUser: ActionEvent = {
        event: 'user_interaction',
        event_data: {
            event_name: name as string,
            ...(event ? { user: event } : {}),
        },
    };

    window.dataLayer.push(enhancedEventWithUser);
}

export function interactionEvent(name: string, event: ActionEventInteraction) {
    if (typeof window === 'undefined' || !window.dataLayer) return;

    const completeEvent: ActionEvent = {
        event: 'user_interaction',
        event_data: {
            event_name: name,
            interaction: event,
        },
    };

    window.dataLayer.push(completeEvent);
}

export function contractEvent(eventName: ContractEventName, payload: ContractEventPayload) {
    if (typeof window === 'undefined' || !window.dataLayer) return;

    window.dataLayer.push({
        event: eventName,
        event_data: payload,
    });
}

export function selectionContratEvent(event: SelectionContratEvent) {
    contractEvent('selection_contrat', event);
}

// Provider TagManager
export function TagManagerProvider({ children }: { children: React.ReactNode }) {
    function getCategory() {
        if (typeof window === 'undefined') return 'portail achat direct';

        const sections: string[] = window.location.pathname
            .toLowerCase()
            .replace(/^\/(en|fr)\//i, '')
            .split('/');

        if (sections[0] === 'appro') {
            return sections[1] ? `appro - ${sections[1]}` : 'appro';
        }

        // Pages hors appro (connexion, inscription, etc.)
        return sections[0] ? `portail achat direct - ${sections[0]}` : 'portail achat direct';
    }

    const value = {
        actionEvent,
        interactionEvent,
        contractEvent,
        selectionContratEvent,
        getCategory,
    };

    return <TagManagerContext.Provider value={value}>{children}</TagManagerContext.Provider>;
}
